import axios from 'axios';
import { isAdminSession, freshRequestKey } from '@/utils/adminFreshness';

const API_BASE_URL = process.env.VUE_APP_API_URL || 'https://kameseeds-api.up.railway.app/api';
const CACHE_VERSION = '1.7'; // Incrementar versión de caché para los nuevos filtros

// Limpiar caché antiguo si es necesario
const initializeCache = () => {
    const storedVersion = localStorage.getItem('cacheVersion');
    if (storedVersion !== CACHE_VERSION) {
        console.log('Nueva versión de caché detectada, limpiando caché antiguo...');
        // Agregar limpieza de caché de actividades
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('activities_') || key === 'activity_stats') {
                localStorage.removeItem(key);
                localStorage.removeItem(`${key}_timestamp`);
            }
        });
        // Limpiar todo el caché relacionado
        localStorage.removeItem('accessToken');
        localStorage.removeItem('products');
        localStorage.removeItem('productsTimestamp');
        localStorage.removeItem('admin_products');
        localStorage.removeItem('admin_productsTimestamp');
        localStorage.removeItem('userOrders');
        localStorage.removeItem('adminOrders');
        localStorage.removeItem('ordersTimestamp');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userPermissionsTimestamp');
        // Establecer nueva versión
        localStorage.setItem('cacheVersion', CACHE_VERSION);
    }
};

// Ejecutar limpieza al iniciar
initializeCache();

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true // Important for CSRF protection
});

/**
 * Requests de la API en vuelo. Lo usa el loader inicial (App.vue) para no
 * destapar la página antes de que el contenido esté cargado.
 */
export const pendingRequests = { count: 0 };

/**
 * Imágenes "críticas" que un componente está precargando a mano y que el
 * loader inicial tiene que esperar igual que un request (ej: el hero de la
 * galería, que es un `background-image` y se pinta recién cuando el navegador
 * lo descargó). Cada componente que suma 1 DEBE restar 1 al terminar.
 */
export const pendingMedia = { count: 0 };

// Interceptor para agregar el token a todas las peticiones
apiClient.interceptors.request.use(config => {
    // Las peticiones secundarias tienen su propio estado de carga en la vista.
    if (!config.skipAppLoader) pendingRequests.count += 1;
    const token = localStorage.getItem('accessToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Una URL nueva evita incluso respuestas públicas guardadas antes del login.
    // No agregamos headers que requieran nuevos permisos CORS.
    if (isAdminSession() && ['get', 'head'].includes((config.method || 'get').toLowerCase())) {
        if (config.params instanceof URLSearchParams) {
            config.params = new URLSearchParams(config.params);
            config.params.set('_fresh', freshRequestKey());
        } else {
            config.params = { ...config.params, _fresh: freshRequestKey() };
        }
    }
    
    // Get the CSRF token from cookies if available
    const csrfToken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
    
    if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
    }
    
    return config;
});

// Cada respuesta (o error) descuenta el request en vuelo
apiClient.interceptors.response.use(
    response => {
        if (!response.config.skipAppLoader) pendingRequests.count = Math.max(0, pendingRequests.count - 1);
        return response;
    },
    error => {
        if (error.config && !error.config.skipAppLoader) pendingRequests.count = Math.max(0, pendingRequests.count - 1);
        return Promise.reject(error);
    }
);

export default apiClient;
